/**
 * RPC URL 管理工具
 * 支持开发环境和生产环境使用不同的 RPC 配置
 * 开发环境：使用 REACT_APP_DEV_RPC_URLS 或 REACT_APP_DEV_RPC_URL
 * 生产环境：使用 REACT_APP_ROC_URLS 或 REACT_APP_RPC_URL
 */

/**
 * 检查是否为开发环境
 * @returns {boolean} 是否为开发环境
 */
function isDevelopment(): boolean {
  return process.env.NODE_ENV === "development";
}

/**
 * 根据环境获取对应的 RPC URLs 配置
 * @returns {string | undefined} 环境变量中的 RPC URLs 字符串
 */
function getRpcUrlsConfig(): string | undefined {
  if (isDevelopment()) {
    // 开发环境优先使用 DEV 配置
    return process.env.REACT_APP_DEV_RPC_URLS || process.env.REACT_APP_ROC_URLS;
  } else {
    // 生产环境使用正常配置
    return process.env.REACT_APP_ROC_URLS;
  }
}

/**
 * 根据环境获取对应的单个 RPC URL 配置
 * @returns {string} 环境变量中的单个 RPC URL
 */
function getSingleRpcUrlConfig(): string {
  if (isDevelopment()) {
    // 开发环境优先使用 DEV 配置
    return process.env.REACT_APP_DEV_RPC_URL || process.env.REACT_APP_RPC_URL || "https://bsc-dataseed.binance.org";
  } else {
    // 生产环境使用正常配置
    return process.env.REACT_APP_RPC_URL || "https://bsc-dataseed.binance.org";
  }
}

/**
 * 从环境变量中随机获取一个 RPC URL
 * @returns {string} 随机选择的 RPC URL
 */
export function getRandomRpcUrl(): string {
  try {
    const rpcUrls = getRpcUrlsConfig();
    const environment = isDevelopment() ? "DEV" : "PROD";

    if (rpcUrls && rpcUrls.trim()) {
      // 分割 URL 列表并去除空格
      const urlList = rpcUrls
        .split(",")
        .map(url => url.trim())
        .filter(url => url);

      if (urlList.length > 0) {
        // 随机选择一个 URL
        const randomIndex = Math.floor(Math.random() * urlList.length);
        const selectedUrl = urlList[randomIndex];

        console.log(`🔗 [${environment}] Random RPC selected: ${selectedUrl} (${randomIndex + 1}/${urlList.length})`);
        return selectedUrl;
      }
    }

    // Fallback 到单个 RPC URL
    const fallbackUrl = getSingleRpcUrlConfig();
    console.log(`🔗 [${environment}] Using fallback RPC: ${fallbackUrl}`);
    return fallbackUrl;
  } catch (error) {
    console.error("❌ Error selecting RPC URL:", error);
    // 出错时使用环境对应的默认配置
    return getSingleRpcUrlConfig();
  }
}

/**
 * 获取所有可用的 RPC URLs
 * @returns {string[]} RPC URL 数组
 */
export function getAllRpcUrls(): string[] {
  const rpcUrls = getRpcUrlsConfig();

  if (rpcUrls && rpcUrls.trim()) {
    return rpcUrls
      .split(",")
      .map(url => url.trim())
      .filter(url => url);
  }

  // Fallback 到单个 URL
  const fallbackUrl = getSingleRpcUrlConfig();
  return [fallbackUrl];
}

/**
 * 获取随机 RPC URL（每次调用都重新随机选择）
 * @returns {string} 新的随机 RPC URL
 */
export function getNewRandomRpcUrl(): string {
  return getRandomRpcUrl();
}

/**
 * 获取当前环境信息和 RPC 配置状态
 * @returns {object} 环境和配置信息
 */
export function getRpcConfigInfo(): {
  environment: string;
  isDev: boolean;
  availableUrls: string[];
  currentUrl: string;
} {
  const isDev = isDevelopment();
  const availableUrls = getAllRpcUrls();
  const currentUrl = getRandomRpcUrl();

  return {
    environment: isDev ? "development" : "production",
    isDev,
    availableUrls,
    currentUrl,
  };
}
